import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '@/state/store';
import type { RootState } from '@/state/store';
import { useComponentList } from '@/components/smartai/api/getComponentList';
import { userSelectedComponent } from '@/state/slices/userSelectedComponentSlice';

/**
 * Resolves the active component from the `:dbRepoId` URL segment and keeps
 * Redux in sync, so the selection survives a refresh / deep link instead of
 * resetting to the slice's empty initial state.
 */
export const useSelectedComponent = () => {
  const { dbRepoId } = useParams<{ dbRepoId: string }>();
  const dispatch = useAppDispatch();
  const { data: list, isPending } = useComponentList();
  const selected = useSelector(
    (state: RootState) => state.userSelectedComponentSlice,
  );

  useEffect(() => {
    if (!dbRepoId || !list?.data) return;
    const found = list.data.find(c => c.dbRepoId === dbRepoId);
    if (!found) return;
    if (
      selected.dbRepoId !== dbRepoId ||
      selected.isWebhookEnabled !== found.isWebhookEnabled ||
      selected.scanId !== found.scanId ||
      selected.provider !== found.provider
    ) {
      dispatch(userSelectedComponent(found));
    }
  }, [
    dbRepoId,
    list?.data,
    selected.dbRepoId,
    selected.isWebhookEnabled,
    selected.scanId,
    selected.provider,
    dispatch,
  ]);

  const ready = !!dbRepoId && selected.dbRepoId === dbRepoId;
  const notFound =
    !!dbRepoId &&
    !isPending &&
    !!list?.data &&
    !list.data.some(c => c.dbRepoId === dbRepoId);

  return {
    selected,
    ready,
    notFound,
    isLoading: isPending && !ready,
    dbRepoId,
  };
};
